import {createContext, useContext, useState, useEffect} from 'react';
import {executeJwtAuthenticationService} from '../api/AuthenticationApiService';
import {apiClient, setGlobalLogout} from '../api/ApiClient';

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export default function AuthProvider({children}) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  async function login(username, password) {
    try {
      const response = await executeJwtAuthenticationService(username, password);
      if (response.status === 200) {
        const jwtToken = response.data.token;
        const returnUsername = response.data.username;
        
        setIsAuthenticated(true);
        setUsername(returnUsername);
        setToken(jwtToken);
        
        storeToken(jwtToken);
        storeUsername(returnUsername);
        
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
        return true;
      } else {
        logout();
        return false;
      }
    } catch (error) {
      console.log('AuthProvider - Login error:', error);
      logout();
      return false;
    }
  }

  // 🆕 ADD: Google login function
  async function loginWithGoogle(credential) {
    try {
      setIsLoading(true);
      
      const backendResponse = await apiClient.post('/auth/google/login', {
        id_token: credential
      });

      if (backendResponse.data && backendResponse.data.token) {
        const jwtToken = backendResponse.data.token;
        const returnUsername = backendResponse.data.user.username;
        
        setIsAuthenticated(true);
        setUsername(returnUsername);
        setToken(jwtToken);
        
        storeToken(jwtToken);
        storeUsername(returnUsername);
        
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
        setIsLoading(false);
        return true;
      }
    } catch (error) {
      console.error('Google login error:', error);
      logout();
      setIsLoading(false);
      return false;
    }
  }

  // 🔧 ENHANCED: Logout with Google support
  function logout(forceGoogleLogout = false) {
    console.log('🔓 Logging out user');
    
    setIsAuthenticated(false);
    setUsername('');
    setToken(null);
    
    removeToken();
    removeUsername();
    removeMemberId();
    
    delete apiClient.defaults.headers.common['Authorization'];

    // 🆕 ADD: Handle Google logout
    try {
      if (window.google?.accounts?.id) {
        // window.google.accounts.id.disableAutoSelect();
      }
      
      if (forceGoogleLogout) {
        // Force Google logout for problem cases
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        // iframe.src = 'https://accounts.google.com/Logout';
        document.body.appendChild(iframe);
        
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }
    } catch (error) {
      console.log('Google logout error:', error);
    }
  }

  // Check if JWT token is expired (web version)
  const isTokenExpired = (token) => {
    try {
      if (!token) return true;
      
      const parts = token.split('.');
      if (parts.length !== 3) return true;
      
      const payload = parts[1];
      const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
      const decodedPayload = atob(paddedPayload);
      const parsedPayload = JSON.parse(decodedPayload);
      
      if (!parsedPayload.exp) return true;
      
      const currentTime = Date.now() / 1000;
      return parsedPayload.exp < currentTime;
    } catch (error) {
      console.error('❌ Error checking token expiration:', error);
      return true;
    }
  };

  // Initialize auth from localStorage on app start
  useEffect(() => {
    console.log('🔄 Initializing web auth...');
    setIsLoading(true);
    
    const storedToken = getToken();
    const storedUsername = getUsername();
    
    if (storedToken && !isTokenExpired(storedToken)) {
      // Token is valid, set up auth
      console.log('✅ Found valid stored token');
      setToken(storedToken);
      setIsAuthenticated(true);
      setUsername(storedUsername || '');
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    } else if (storedToken && isTokenExpired(storedToken)) {
      // Token is expired, clear everything
      console.log('⚠️ Stored token is expired, clearing');
      logout();
    } else {
      console.log('ℹ️ No stored token found');
    }
    
    setIsLoading(false);
  }, []);

  // Set up global logout function for ApiClient
  useEffect(() => {
    setGlobalLogout(logout);
  }, []);

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      setIsAuthenticated,
      login,
      loginWithGoogle, // 🆕 ADD THIS
      logout,
      username,
      setUsername,
      token,
      setToken,
      isLoading 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// Helper functions to manage token and member_id in localStorage
const storeToken = (token) => {
  localStorage.setItem('token', token);
};

const getToken = () => {
  return localStorage.getItem('token');
};

const removeToken = () => {
  localStorage.removeItem('token');
};

const storeUsername = (returnUsername) => {
  localStorage.setItem('username', returnUsername);
};

const getUsername = () => {
  return localStorage.getItem('username');
};

const removeUsername = () => {
  localStorage.removeItem('username');
};

const storeMemberId = (memberId) => {
  localStorage.setItem('member_id', memberId);
};

const removeMemberId = () => {
  localStorage.removeItem('member_id');
};